import { styled } from '@mui/material';

import { DocumentWrapper } from '../components/DocumentWrapper';

const StyledLastUpdated = styled('p')({
  marginTop: 0,
  fontSize: 11,
  color: 'var(--color-neutral-500)',
});

export const PrivacyPolicy = () => {
  return (
    <DocumentWrapper>
      <StyledLastUpdated>Last updated: June 25, 2024</StyledLastUpdated>

      <h3>Introduction and Overview</h3>
      <p>
        AppVelocity Inc. (“<b>AppVelocity</b>,” “<b>we</b>,” “<b>us</b>,” or “
        <b>our</b>”) respects your privacy and is committed to protecting it
        through our compliance with this Privacy Policy.
      </p>
      <p>
        This Privacy Policy describes how we collect, use and share information
        about you as well as your rights and choices about such collection, use
        and sharing, and applies to your use of services provided by AppVelocity or
        your interactions with our website at{' '}
        <a href="https://www.appvelocity.io">www.appvelocity.io</a> (including
        any successor site, the “<b>Site</b>”), our hosted software platform and
        other tools (including APIs), documentation and updates to each of them
        (collectively, the “<b>Services</b>”).
      </p>
      <p>
        Please read this Privacy Policy carefully to understand our policies and
        practices regarding your information and how we will treat it. If you do
        not agree with our policies and practices, your choice is not to use the
        Services. By accessing or using the Services, you agree to this Privacy
        Policy.
      </p>
      <p>
        This Privacy Policy may change from time to time (see Changes to this
        Privacy Policy). Your continued use of the Services after we make
        changes is deemed to be acceptance of those changes, so please check
        this Privacy Policy periodically for updates. If you have any questions
        about our privacy practices, please contact us as set forth in the
        Contact Us section below.
      </p>
      <h3>Information Collection</h3>
      <p>
        We collect the following types of information from users of the
        Services.
      </p>
      <ol>
        <li>
          <strong>
            <em>Information You Provide to AppVelocity</em>
          </strong>
          <p>
            We collect certain information you provide directly via the
            Services:
            <ul>
              <li>
                Your contact information when you create an account or contact
                us by email, chat, telephone, or via a contact form.
              </li>
              <li>
                Professional information about you, including your employer.
              </li>
              <li>
                The content of your communications with us as necessary to
                provide you with the Services.
              </li>
              <li>
                If you enable third-party account access to us via the Services,
                we may gain access to personal information relating to you or
                users of the apps to which we have access. Any personal
                information accessed through third-party account access will not
                be used by AppVelocity for any purpose other than to provide the
                Services.
              </li>
            </ul>
          </p>
        </li>
        <li>
          <strong>
            <em>Information Collected Automatically</em>
          </strong>
          <p>
            As you navigate through and interact with the Services, we may use
            automatic data collection technologies to collect certain
            information about your equipment, browsing actions and patterns,
            including:
          </p>
          <ul>
            <li>
              Details of your use of the Services, including traffic data, logs,
              and other communication data and the resources that you access and
              use on the Services.
            </li>
            <li>
              Information about the type of device or browser you use, your
              device’s operating software, your internet service provider, your
              device’s regional and language settings, and other similar
              information. This data may include IP address, MAC address, device
              advertising ID (e.g., IDFA or AAID), and other device identifiers.
            </li>
          </ul>
          <p>
            The information we collect automatically helps us to improve the
            Services, including by enabling us to:
          </p>
          <ul>
            <li>Estimate our audience size and usage patterns.</li>
            <li>
              Store information about your preferences, allowing us to customize
              the Services according to your individual interests.
            </li>
            <li>Recognize you when you return to the Services.</li>
          </ul>
          <p>
            The technologies we use for this automatic data collection may
            include:
          </p>
          <ul>
            <li>
              <em>Cookies (or browser cookies).</em> A cookie is a small file
              placed on the hard drive of your computer. You may refuse to
              accept browser cookies by activating the appropriate setting on
              your browser. However, if you select this setting, you may be
              unable to access certain parts of the Services. Unless you have
              adjusted your browser setting so that it will refuse cookies, our
              system will issue cookies when you direct your browser to the
              Services.
            </li>
            <li>
              <em>Flash Cookies.</em> Certain features of the Services may use
              local stored objects (or Flash cookies) to collect and store
              information about your preferences and navigation to, from, and on
              the Services. Flash cookies are not managed by the same browser
              settings as are used for browser cookies.
            </li>
            <li>
              <em>Web Beacons.</em> Pages of our Site may contain small
              electronic files known as web beacons (also referred to as clear
              gifs, pixel tags, and single-pixel gifs) that permit us, for
              example, to count users who have visited those pages and for other
              related website statistics (for example, recording the popularity
              of certain website content and verifying system and server
              integrity).
            </li>
          </ul>
        </li>
      </ol>
      <h3>Use of Information</h3>
      <p>
        We use information that we collect about you or that you provide to us,
        including any personal information:
      </p>
      <ul>
        <li>
          To provide the Services to you, including to provide customer support.
        </li>
        <li>
          To provide you with information, products, or services that you
          request from us.
        </li>
        <li>To fulfill any other purpose for which you provide it.</li>
        <li>
          If you have an account with AppVelocity, to provide you with notices
          about your account.
        </li>
        <li>
          To carry out our obligations and enforce our rights arising from any
          contracts entered into between you and us, including for billing and
          collection, and arising under applicable law.
        </li>
        <li>
          To notify you about changes to the Services or any products or
          services we offer or provide though it.
        </li>
        <li>
          In any other way we may describe when you provide the information.
        </li>
        <li>For any other purpose with your consent.</li>
      </ul>
      <p>
        We may also use your information to contact you about our goods and
        services that may be of interest to you. If you do not want us to use
        your information in this way, please let us know at{' '}
        <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a>.
      </p>
      <p>
        We may use information that does not identify you (including information
        that has been de-identified) without obligation to you except as
        prohibited by applicable law.
      </p>
      <p>
        For information on your rights and choices regarding how we use your
        information, see <em>Your Rights and Choices</em> below.
      </p>

      <h3>Sharing of Information</h3>
      <p>We share information about you as follows:</p>
      <ul>
        <li>
          <em>Service Providers</em>. We may share your information with our
          agents, vendors and other service providers (collectively “Service
          Providers”) in connection with their work on our behalf. Service
          Providers assist us with services such as payment processing, credit
          checks, data analytics, marketing and promotional services, website
          hosting, and technical support. Service Providers are prohibited from
          using your information for any purpose other than to provide this
          assistance, although we may permit them to use aggregate information
          which does not identify you or de-identified data for other purposes.
        </li>
        <li>
          <em>Affiliates</em>. We may share your information with our related
          entities, including our parent and sister companies. For example, we
          may share your information with our affiliates for customer support,
          marketing and technical operations.
        </li>
        <li>
          <em>Business Partners</em>. We may share your information with our
          business partners in connection with offering you co-branded services,
          selling or distributing the Services, or engaging in joint marketing
          activities.
        </li>
        <li>
          <em>Merger or Acquisition</em>. We may share your information in
          connection with, or during negotiations of, any proposed or actual
          merger, purchase, sale or any other type of acquisition or business
          combination of all or any portion of our assets, or transfer of all or
          a portion of our business to another business.
        </li>
        <li>
          <em>Security and Compelled Disclosure</em>. We may share your
          information to comply with the law or other legal process, and where
          required, in response to lawful requests by public authorities,
          including to meet national security or law enforcement requirements.
          We may also share your information to protect the rights, property,
          life, health, security and safety of AppVelocity, the Services or any
          third party.
        </li>
        <li>
          <em>Consent</em>. We may share your information for any other purpose
          disclosed to you and with your consent.
        </li>
      </ul>
      <p>
        Without limiting the foregoing, in our sole discretion, we may share
        aggregated information that does not identify you or de-identified
        information about you with third parties or affiliates for any purpose
        except as prohibited by applicable law. For information on your rights
        and choices regarding how we share your information, please see Your
        Rights and Choices below.
      </p>
      <h3>Your Rights and Choices</h3>
      <ol>
        <li>
          <strong>
            <em>Review and Update of Account Information</em>
          </strong>
          <p>
            You may access, update, or remove certain account information that
            you have voluntarily submitted to us through the Services by sending
            an email to us at{' '}
            <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a>.
            We may require additional information from you to allow us to
            confirm your identity.
          </p>
          <p>
            Please note that we will retain and use your information as
            necessary to comply with our legal obligations, resolve disputes,
            and enforce our agreements.
          </p>
        </li>
        <li>
          <strong>
            <em>Tracking Technology Choices</em>
          </strong>
          <p>
            <em>Cookies and Pixels.</em> Most browsers accept cookies by
            default. You can instruct your browser, by changing its settings, to
            decline or delete cookies. If you use multiple browsers on your
            device, you will need to instruct each browser separately. Your
            ability to limit cookies is subject to your browser settings and
            limitations.
          </p>
          <em>Do Not Track.</em> Your browser settings may allow you to
          automatically transmit a “Do Not Track” signal to online services you
          visit. Note, however, there is no industry consensus as to what site
          and app operators should do with regard to these signals. Accordingly,
          we do not monitor or take action with respect to “Do Not Track”
          signals or other mechanisms.
          <p>
            Please be aware that if you disable or remove tracking technologies,
            some parts of the Services may not function correctly.
          </p>
        </li>
        <li>
          <strong>
            <em>Communications</em>
          </strong>
          <p>
            You can opt-out of receiving promotional emails from us at any time
            by following the instructions as provided in emails to click on the
            unsubscribe link, or emailing us at{' '}
            <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a>{' '}
            with the word UNSUBSCRIBE in the subject field of the email. Please
            note that you cannot opt-out of certain non-promotional emails, such
            as those about your account, transactions, servicing, or AppVelocity’s
            ongoing business relations with you.
          </p>
        </li>
      </ol>
      <h3>Your European Privacy Rights</h3>
      <p>
        If you are a resident of the European Union, you are entitled to certain
        information and have certain rights under the General Data Protection
        Regulation (Regulation (EU) 2016/679) (the “<b>GDPR</b>”). Those rights
        include:
      </p>
      <ul>
        <li>The right of access to your information.</li>
        <li>
          The right to rectify your information if it is incorrect or
          incomplete.
        </li>
        <li>
          The right to have your information erased (“right to be forgotten”) if
          certain grounds are met.
        </li>
        <li>
          The right to withdraw your consent to our processing of your
          information at any time (if our processing is based on consent).
        </li>
        <li>
          The right to object to our processing of your information (if
          processing is based on legitimate interests).
        </li>
        <li>
          The right to object to our processing of your information for direct
          marketing purposes.
        </li>
        <li>
          The right to receive your information from us in a structured,
          commonly used and machine-readable format, and the right to transmit
          your information to another controller without hindrance from us (data
          portability).
        </li>
      </ul>
      <p>
        If you are located in the European Union and you are or have been a user
        of the Services, we may send you marketing communications based on our
        legitimate interests, subject always to your right to opt out of such
        communications. Further, if you are located in the European Union, we
        will never share your information with a third party for such third
        party’s marketing purposes, unless you have specifically consented to us
        doing so.
      </p>
      <p>
        You may contact us at{' '}
        <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a> to
        exercise any of the above rights. We may request specific information
        from you to confirm your identity, and in some circumstances, we may
        charge a reasonable fee for access to your information.
      </p>
      <p>
        Furthermore, if you believe that our processing of your information is
        inconsistent with your data protection rights under the GDPR and we have
        not adequately addressed your concerns, you have the right to lodge a
        complaint with the data protection supervisory authority of your
        country.
      </p>
      <p>
        For purposes of the GDPR, we are a “controller” and you are a “data
        subject.”
      </p>
      <h3>Visitors Outside the United States</h3>
      <p>
        AppVelocity is a U.S.-based AppVelocity. If you are a non-U.S. resident and
        provide us with your personal information, you acknowledge and agree
        that your personal information may be transferred to and processed in
        the United States, where the laws regarding processing of personal
        information may be less stringent than the laws in your country. By
        providing your information, you consent to such transfer and processing.
      </p>
      <h3>Children</h3>
      <p>
        The Services are intended for a general audience and are not directed to
        children under 13 years of age. AppVelocity does not knowingly collect
        personal information as defined by the Children’s Online Privacy
        Protection Act (“COPPA”) in a manner that is not permitted by COPPA. If
        you are a parent or guardian and believe AppVelocity has collected such
        information in a manner not permitted by COPPA, please contact us at{' '}
        <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a> and
        we will remove such information to the extent required by COPPA.
      </p>
      <h3>Data Security</h3>
      <p>
        We have implemented measures designed to secure your personal
        information from accidental loss and from unauthorized access, use,
        alteration, and disclosure.
      </p>
      <p>
        Unfortunately, the transmission of information via the internet is not
        completely secure. Although we do our best to protect your personal
        information, we cannot guarantee the security of your personal
        information transmitted to the Services. Any transmission of personal
        information is at your own risk. We are not responsible for
        circumvention of any privacy settings or security measures contained on
        the Services.
      </p>
      <h3>Changes to this Privacy Policy</h3>
      <p>
        We reserve the right to update or make changes to this Privacy Policy
        from time to time in our sole discretion. Any changes will be effective
        immediately upon posting of the revised Privacy Policy and updating the
        “Updated” date above. If the changes are material, we may provide you
        additional notice to your email address, if we have one on file for you.
        Please return to this Privacy Policy to ensure familiarity with the most
        current version of this Privacy Policy. Your continued access or use of
        the Services after any changes to this Privacy Policy have been posted
        shall constitute your agreement to be bound by the then current version
        of this Privacy Policy in full. If you do not wish to accept changes to
        this Privacy Policy, you may discontinue your use of the Services and
        close your account with us.
      </p>
      <h3>Contact Us</h3>
      <p>
        If you have any questions or comments about this Privacy Policy or our
        privacy practices, please email us at{' '}
        <a href="mailto:contact@appvelocity.io">contact@appvelocity.io</a>.
      </p>
    </DocumentWrapper>
  );
};
